import { Fragment, useCallback, useEffect, useState } from "react";
import Spinner from "../../components/ui/Spinner";
import { btnGhost, btnPrimary, cardCls, errMsg, inputCls, labelCls } from "../../components/admin/promotionsUi";
import { useAdminToast } from "../../context/AdminToastContext";
import { formatISTDateTime } from "../../utils/dateUtils";
import { gamificationService } from "../../features/gamification/service";
import type { AuditRow } from "../../features/gamification/types";

import { LoadError, Pager, PersonCell, dash, tdCls, thCls, usePaged } from "./gamificationShared";

// ── Audit rows (take-backs and the full gm_audit log) ───────────────────────
const ACTION_LABEL: Record<string, string> = {
  "config.update": "Settings changed",
  "quest.update": "Quest changed",
  "wallet.takeback": "Take-back requested",
  "wallet.takeback.taken_back": "Taken back",
  "wallet.takeback.already_taken_back": "Already taken back",
  "wallet.takeback.below_floor": "Below the floor, flagged for review",
  "wallet.takeback.window_closed": "Window closed",
  "wallet.takeback.not_takeable": "Not a bonus credit",
  "wallet.takeback.no_lot": "No credit record",
  "wallet.takeback.busy": "Busy, try again",
  "approval.requested": "Approval requested",
  "approval.approved": "Approval granted",
  "approval.rejected": "Approval rejected",
  "approval.withdrawn": "Approval withdrawn",
  "badge.award": "Badge awarded",
  "badge.revoke": "Badge revoked",
  "review.retry": "Review queue: retry",
  "review.dismissed": "Review queue: dismissed",
  "intl_verification.approved": "Document approved",
  "intl_verification.rejected": "Document rejected",
};

export function AuditTable({ rows, showUser }: { rows: AuditRow[]; showUser?: boolean }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="overflow-x-auto mt-2">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="text-xs text-gray-500">
            <th className={thCls}>When</th>
            <th className={thCls}>Who</th>
            <th className={thCls}>What</th>
            <th className={thCls}>{showUser ? "User" : "User or target"}</th>
            <th className={thCls}>Reason</th>
            <th className="pb-2 font-medium" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Fragment key={r.id}>
              <tr className="border-t border-gray-800 text-gray-300 align-top">
                <td className={`${tdCls} whitespace-nowrap`}>{formatISTDateTime(r.created_at)}</td>
                <td className={tdCls}>
                  <PersonCell id={r.actor} name={r.actor_name} email={r.actor_email} phone={r.actor_phone} fallback="—" />
                  {r.system && <span className="text-gray-500 text-xs"> (system)</span>}
                </td>
                <td className={tdCls}>{ACTION_LABEL[r.action] ?? r.action}</td>
                <td className={`${tdCls} max-w-[18rem]`}>
                  {r.user_id ? (
                    <PersonCell id={r.user_id} name={r.user_name} email={r.user_email} phone={r.user_phone} />
                  ) : (
                    <span className="break-all text-xs text-gray-400">{showUser ? "—" : dash(r.target)}</span>
                  )}
                </td>
                <td className={`${tdCls} max-w-[16rem]`}>{dash(r.reason)}</td>
                <td className="py-2">
                  {(r.before || r.after) && (
                    <button className={btnGhost} onClick={() => setOpen(open === r.id ? null : r.id)}>{open === r.id ? "Hide" : "Details"}</button>
                  )}
                </td>
              </tr>
              {open === r.id && (
                <tr className="text-xs text-gray-400">
                  <td colSpan={6} className="pb-3">
                    <pre className="whitespace-pre-wrap break-all bg-gray-950 border border-gray-800 rounded-lg p-3">{JSON.stringify({ before: r.before, after: r.after }, null, 2)}</pre>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
export function AuditPanel() {
  const toast = useAdminToast();
  const exportCsv = async (run: () => Promise<void>, what: string) => {
    try {
      await run();
    } catch (e) {
      toast.showError(errMsg(e, `Failed to export the ${what}`));
    }
  };
  const [action, setAction] = useState("");
  const [applied, setApplied] = useState("");
  const fetcher = useCallback((page: number) => gamificationService.listAudit(applied, page), [applied]);
  const { data, loading, error, load } = usePaged(fetcher, "Failed to load the audit log");
  useEffect(() => { void load(1); }, [load]);

  return (
    <section className={cardCls}>
      <h2 className="text-white font-semibold mb-1">Audit log</h2>
      <p className="text-gray-500 text-xs mb-4">
        Every settings change, quest edit and take-back, with who did it. Read-only.
        <span className="block text-gray-600">Example: type "quest" to see only quest edits, or "wallet" for take-backs.</span>
      </p>
      <form className="flex flex-wrap gap-2 items-end mb-2" onSubmit={(e) => { e.preventDefault(); setApplied(action.trim()); }}>
        <div>
          <label className={labelCls} htmlFor="ga-action">Action starts with</label>
          <input id="ga-action" className={`${inputCls} max-w-xs`} value={action} onChange={(e) => setAction(e.target.value)} placeholder="config, quest, wallet" />
        </div>
        <button type="submit" className={btnPrimary} disabled={loading}>Filter</button>
        <button type="button" className={btnGhost} onClick={() => void exportCsv(() => gamificationService.exportAudit(applied), "audit log")}>Export CSV</button>
      </form>
      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : !data ? (
        <LoadError message={error} onRetry={() => void load(1)} />
      ) : data.items.length === 0 ? (
        <p className="text-gray-500 text-sm py-4">No audit rows yet.</p>
      ) : (
        <>
          <AuditTable rows={data.items} />
          <Pager data={data} busy={loading} onPage={(p) => void load(p)} />
        </>
      )}
    </section>
  );
}
